import {useMemo} from "react";
import {ExceedsNonPrimaryVolumeByTimeProps} from "./ExceedsNonPrimaryVolumeByTimeInsight";
import {getDateFormatterOptions, TimeBucketData, TimeChartData} from "../../chart/TimeChart/TimeChart";
import {
  addTime,
  adjustFromTimeZone,
  getDefaultTimeFrame,
  getTimeFrameDates,
  isBefore,
  startOf
} from "@ots-frontend/ots-data";

export type UseExceedsNonPrimaryVolumeByTimeInsightProps = Omit<ExceedsNonPrimaryVolumeByTimeProps, 'id' | 'title' | 'showEmpty' | 'xAxisTitle' | 'yAxisTitle'>

export type UseExceedsNonPrimaryVolumeByTimeInsightResult = {
  chartData?: TimeChartData
}

export function useExceedsNonPrimaryVolumeByTimeInsight(props: UseExceedsNonPrimaryVolumeByTimeInsightProps): UseExceedsNonPrimaryVolumeByTimeInsightResult {
  const {
    fromDttm,
    toDttm = new Date().getTime(),
    data,
    display,
    category,
  } = props

  const timeFrame = useMemo(() => {
    const defaultTimeFrame = getDefaultTimeFrame(fromDttm, toDttm)
    if (defaultTimeFrame === 'MONTH' || defaultTimeFrame === 'WEEK') {
      return "DAY"
    }
    return defaultTimeFrame
  }, [fromDttm, toDttm])

  const chartData: TimeChartData | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }

    const dates: string[] = []
    const buckets: Record<string, TimeBucketData> = {}

    const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)

    let startDttm = timeFrameDates.startDttm
    const endDttm = timeFrameDates.endDttm

    const formatter = new Intl.DateTimeFormat(undefined, getDateFormatterOptions(timeFrame))

    while (isBefore(startDttm, endDttm)) {
      dates.push(formatter.format(startDttm))

      data.forEach((snapshot) => {
        if (!category || category.cargoSubtype === snapshot.cargoSubtype) {
          const objectName = snapshot.objectName ?? "Unknown"
          const entry = snapshot.entries.find((entry) => startOf(timeFrame, adjustFromTimeZone(entry.dttm)).getTime() === startDttm.getTime())
          const value = display === "count" ? entry?.count : entry?.volume

          if (objectName in buckets) {
            buckets[objectName].values.push(value)
          } else {
            buckets[objectName] = {
              values: [value]
            }
          }
        }
      })

      startDttm = addTime(timeFrame, startDttm, 1)
    }

    return {
      titles: dates,
      buckets: buckets,
    }
  }, [data, timeFrame, fromDttm, toDttm, category])

  return {
    chartData: chartData
  };
}
